import {createSlice} from '@reduxjs/toolkit';
import {
  fetchEvents,
  fetchFutureEvents,
  fetchPastEvents,
  fetchDrafts,
  deleteDraftThunk,
} from './EventThunks';
import {DocumentSnapshot} from 'firebase/firestore';
import {EventDTO} from '../../models/event/EventDTO';
import {DraftDTO} from '../../models/event/DraftDTO';

interface EventState {
  events: EventDTO[];
  futureEvents: EventDTO[];
  pastEvents: EventDTO[];
  drafts: DraftDTO[];
  lastVisible: DocumentSnapshot<EventDTO> | null;
  lastVisiblePast: DocumentSnapshot<EventDTO> | null;
  loading: boolean;
  loadingPast: boolean;
  loadingDrafts: boolean;
  error: string | null;
  hasMore: boolean;
  hasMorePast: boolean;
  hasLoadedFutureEvents: boolean;
  hasLoadedPastEvents: boolean;
  hasLoadedDrafts: boolean;
}

const initialState: EventState = {
  events: [],
  futureEvents: [],
  pastEvents: [],
  drafts: [],
  lastVisible: null,
  lastVisiblePast: null,
  loading: false,
  loadingPast: false,
  loadingDrafts: false,
  error: null,
  hasMore: true,
  hasMorePast: true,
  hasLoadedFutureEvents: false,
  hasLoadedPastEvents: false,
  hasLoadedDrafts: false,
};

export const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    resetEvents: state => {
      state.events = [];
      state.futureEvents = [];
      state.pastEvents = [];
      state.lastVisible = null;
      state.lastVisiblePast = null;
      state.hasMore = true;
      state.hasMorePast = true;
      state.hasLoadedFutureEvents = false;
      state.hasLoadedPastEvents = false;
    },
    addEvent: (state, action) => {
      const newEvent = action.payload;
      const eventDate = new Date(newEvent.startDateTime.toDate());
      const today = new Date();
      today.setHours(0, 1, 0, 0);

      // Add to the appropriate array based on event date
      if (eventDate >= today) {
        // Future event (including today from 00:01 onwards)
        state.futureEvents = [newEvent, ...state.futureEvents];
      } else {
        // Past event (before today 00:01)
        state.pastEvents = [newEvent, ...state.pastEvents];
      }

      // Also add to the old events array for backward compatibility
      state.events = [newEvent, ...state.events];
    },
    updateEvent: (state, action) => {
      const updatedEvent = action.payload;
      const eventDate = new Date(updatedEvent.startDateTime.toDate());
      const today = new Date();
      today.setHours(0, 1, 0, 0);

      // Remove the old event from both arrays first
      if (state.futureEvents.length > 0) {
        state.futureEvents = state.futureEvents.filter(
          event => event.id !== updatedEvent.id,
        );
      }

      if (state.pastEvents.length > 0) {
        state.pastEvents = state.pastEvents.filter(
          event => event.id !== updatedEvent.id,
        );
      }

      // Add the updated event to the appropriate array
      if (eventDate >= today) {
        // Future event (including today from 00:01 onwards)
        state.futureEvents = [updatedEvent, ...state.futureEvents];
      } else {
        // Past event (before today 00:01)
        state.pastEvents = [updatedEvent, ...state.pastEvents];
      }

      // Also update in the old events array for backward compatibility
      const eventIndex = state.events.findIndex(
        event => event.id === updatedEvent.id,
      );
      if (eventIndex !== -1) {
        state.events[eventIndex] = updatedEvent;
      }
    },
    removeEvent: (state, action) => {
      const eventId = action.payload;
      state.events = state.events.filter(event => event.id !== eventId);
      state.futureEvents = state.futureEvents.filter(
        event => event.id !== eventId,
      );
      state.pastEvents = state.pastEvents.filter(event => event.id !== eventId);
    },
    setDrafts: (state, action) => {
      state.drafts = action.payload;
    },
    addDraft: (state, action) => {
      state.drafts = [action.payload, ...state.drafts];
    },
    updateDraft: (state, action) => {
      const updatedDraft = action.payload;
      const draftIndex = state.drafts.findIndex(
        draft => draft.id === updatedDraft.id,
      );
      if (draftIndex !== -1) {
        state.drafts[draftIndex] = updatedDraft;
      }
    },
    removeDraft: (state, action) => {
      const draftId = action.payload;
      state.drafts = state.drafts.filter(draft => draft.id !== draftId);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEvents.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.hasMore = action.payload.lastVisible !== null;

        // Check if it's the next page or first page
        if (action.payload.isNextPage) {
          state.events = [...state.events, ...action.payload.events]; // Append new events for pagination
        } else {
          state.events = action.payload.events; // Overwrite events on the first page load
        }

        state.lastVisible = action.payload.lastVisible; // Update the lastVisible for pagination
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false;
        state.hasMore = false;
        state.error = action.error.message || 'Error fetching events';
      })
      .addCase(fetchFutureEvents.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFutureEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.hasLoadedFutureEvents = true;
        state.futureEvents = action.payload.events;
      })
      .addCase(fetchFutureEvents.rejected, (state, action) => {
        state.loading = false;
        state.hasMore = false;
        state.error = action.error.message || 'Error fetching future events';
      })
      .addCase(fetchPastEvents.pending, state => {
        state.loadingPast = true; // Set loading for past events
        state.error = null;
      })
      .addCase(fetchPastEvents.fulfilled, (state, action) => {
        state.loadingPast = false; // Reset loading for past events
        state.hasLoadedPastEvents = true;
        state.hasMorePast = action.payload.lastVisiblePast !== null;

        // Check if it's the next page or first page
        if (action.payload.isNextPage) {
          // Append new events for pagination
          state.pastEvents = [...state.pastEvents, ...action.payload.events];
        } else {
          // Overwrite events on the first page load
          state.pastEvents = action.payload.events;
        }

        state.lastVisiblePast = action.payload.lastVisiblePast; // Update the lastVisiblePast for pagination
      })
      .addCase(fetchPastEvents.rejected, (state, action) => {
        state.loadingPast = false; // Reset loading for past events
        state.hasMorePast = false;
        state.error = action.error.message || 'Error fetching past events';
      })
      .addCase(fetchDrafts.pending, state => {
        state.loadingDrafts = true;
        state.error = null;
      })
      .addCase(fetchDrafts.fulfilled, (state, action) => {
        state.loadingDrafts = false;
        state.hasLoadedDrafts = true;
        state.drafts = action.payload;
      })
      .addCase(fetchDrafts.rejected, (state, action) => {
        state.loadingDrafts = false;
        state.error = action.error.message || 'Error fetching drafts';
      })
      .addCase(deleteDraftThunk.fulfilled, (state, action) => {
        state.drafts = state.drafts.filter(
          draft => draft.id !== action.payload,
        );
      });
  },
});

export const {
  resetEvents,
  addEvent,
  updateEvent,
  removeEvent,
  setDrafts,
  addDraft,
  updateDraft,
  removeDraft,
} = eventSlice.actions;

export const EventReducer = eventSlice.reducer;
